import { useState } from "react";
import { Lock, Unlock } from "lucide-react";
import { toast } from "sonner";
import { eventsApi } from "../lib/events";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";

const LABELS = [
  { value: "SOLD_OUT", text: "Sold out" },
  { value: "SALES_CLOSED", text: "Sales closed" },
  { value: "COMING_SOON", text: "Coming soon" },
  { value: "CUSTOM", text: "Custom message" },
];

function extractErr(e) {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (d?.message) return d.message;
  return "Something went wrong";
}

/**
 * Manual "close ticket sales" control (event- or tier-level).
 * props: scope ("event"|"ticket_type"), eventId, ttId?, current {sales_closed, sales_closed_label, sales_closed_message}, label (button context), onChanged()
 */
export default function SalesStatusControl({ scope, eventId, ttId, current = {}, contextLabel, onChanged }) {
  const isClosed = !!current.sales_closed;
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("close"); // "close" | "reopen" | "update"
  const [label, setLabel] = useState(current.sales_closed_label || "SALES_CLOSED");
  const [message, setMessage] = useState(current.sales_closed_message || "");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const start = (m) => {
    setMode(m);
    setLabel(current.sales_closed_label || "SALES_CLOSED");
    setMessage(current.sales_closed_message || "");
    setReason("");
    setOpen(true);
  };

  const submit = async () => {
    if (!reason.trim()) { toast.error("Please add an internal reason"); return; }
    const closed = mode !== "reopen";
    if (closed && label === "CUSTOM" && (message.trim().length < 1 || message.trim().length > 120)) {
      toast.error("Custom message must be 1–120 characters"); return;
    }
    const body = { closed, label: closed ? label : null, message: closed && label === "CUSTOM" ? message.trim() : null, reason: reason.trim() };
    setBusy(true);
    try {
      if (scope === "event") await eventsApi.setEventSalesStatus(eventId, body);
      else await eventsApi.setTicketTypeSalesStatus(eventId, ttId, body);
      toast.success(closed ? "Ticket sales closed" : "Ticket sales reopened");
      setOpen(false);
      onChanged && onChanged();
    } catch (e) { toast.error(extractErr(e)); }
    finally { setBusy(false); }
  };

  const closedText = current.sales_closed_message
    || (current.sales_closed_label === "SOLD_OUT" ? "Sold out"
      : current.sales_closed_label === "COMING_SOON" ? "Coming soon" : "Sales closed");

  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid={`sales-status-${scope}${ttId ? "-" + ttId : ""}`}>
      {isClosed ? (
        <>
          <span className="inline-flex items-center gap-1 text-xs font-bold px-2 py-1 bg-amber-100 text-amber-800 border border-amber-300"
            data-testid={`sales-closed-badge-${scope}${ttId ? "-" + ttId : ""}`}>
            <Lock size={12} /> {closedText}
          </span>
          <Button size="sm" variant="outline" onClick={() => start("update")}
            data-testid={`sales-edit-btn-${scope}${ttId ? "-" + ttId : ""}`}>Edit message</Button>
          <Button size="sm" variant="outline" onClick={() => start("reopen")}
            data-testid={`sales-reopen-btn-${scope}${ttId ? "-" + ttId : ""}`}>
            <Unlock size={12} className="mr-1" /> Reopen sales
          </Button>
        </>
      ) : (
        <Button size="sm" variant="outline" onClick={() => start("close")}
          data-testid={`sales-close-btn-${scope}${ttId ? "-" + ttId : ""}`}>
          <Lock size={12} className="mr-1" /> Close ticket sales
        </Button>
      )}

      <Dialog open={open} onOpenChange={(v) => !v && setOpen(false)}>
        <DialogContent className="max-w-md" data-testid={`sales-status-dialog-${scope}`}>
          <DialogHeader>
            <DialogTitle className="font-heading font-black">
              {mode === "reopen" ? "Reopen ticket sales" : mode === "update" ? "Update closed message" : "Close ticket sales"}
            </DialogTitle>
            <DialogDescription>
              {contextLabel ? `${contextLabel} — ` : ""}
              {mode === "reopen"
                ? "Buyers will be able to purchase again (existing tickets, scans and payments are unaffected)."
                : "Blocks new purchases. Existing tickets, scans, payments and the waitlist are unaffected."}
            </DialogDescription>
          </DialogHeader>

          {mode !== "reopen" && (
            <div className="space-y-3">
              <div>
                <label className="gt-label mb-1 block">Message shown to buyers</label>
                <select className="gt-input w-full" value={label} onChange={(e) => setLabel(e.target.value)}
                  data-testid="sales-status-label">
                  {LABELS.map((l) => <option key={l.value} value={l.value}>{l.text}</option>)}
                </select>
              </div>
              {label === "CUSTOM" && (
                <div>
                  <input className="gt-input w-full" maxLength={120} value={message}
                    onChange={(e) => setMessage(e.target.value)} placeholder="Plain text, max 120 chars"
                    data-testid="sales-status-custom" />
                  <div className="text-[11px] text-[var(--gt-ink-3)] mt-1">{message.trim().length}/120</div>
                </div>
              )}
            </div>
          )}
          <input className="gt-input w-full mt-2" value={reason} onChange={(e) => setReason(e.target.value)}
            placeholder="Internal reason (audit-only, never shown to buyers)" data-testid="sales-status-reason" />

          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} disabled={busy}>Cancel</Button>
            <Button onClick={submit} disabled={busy} data-testid="sales-status-submit">
              {busy ? "Saving…" : mode === "reopen" ? "Reopen sales" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
